/** @jsxImportSource @emotion/react */
import {
    Children,
    cloneElement,
    createRef,
    isValidElement,
    useCallback,
    useContext,
    useMemo,
    useRef,
  } from 'react';
  import type { ReactElement, ReactNode, RefObject } from 'react';
  import {
    createRoutesFromElements,
    matchRoutes,
    Navigate,
    Route,
    UNSAFE_RouteContext,
    useLocation,
    useRoutes,
  } from 'react-router-dom';
  import type { NavigateProps, RouteObject, RouteProps } from 'react-router-dom';
  import { CSSTransition, TransitionGroup } from 'react-transition-group';
  import type { CSSTransitionProps } from 'react-transition-group/CSSTransition';
  import { css } from '@emotion/react';
  
  type Direction = 'forward' | 'back' | 'undirected';
  type RouteElement = ReactElement<RouteProps, typeof Route>;
  type ChildElement = RouteElement | ReactElement<NavigateProps, typeof Navigate>;
  type RouteItem = Required<RouteObject> & {
    element: ReactElement & { ref: RefObject<HTMLDivElement> };
  };
  
  const getTransformStyles = (transformFn: string, max: string) => `
    // back
    & > .back-enter {
      transform: ${transformFn}(-${max});
    }
    & > .back-enter-active {
      transform: ${transformFn}(0);
    }
    & > .back-exit {
      transform: ${transformFn}(0);
    }
    & > .back-exit-active {
      transform: ${transformFn}(${max});
    }
    & > .back-exit-active .black-box {
      opacity: 0.6;
    }
  
    // forward
    & > .forward-enter {
      transform: ${transformFn}(${max});
    }
    & > .forward-enter-active {
      transform: ${transformFn}(0);
    }
    & > .forward-exit {
      transform: ${transformFn}(0);
    }
    & > .forward-exit-active {
      transform: ${transformFn}(-${max});
    }
    & > .forward-exit-active .black-box {
      opacity: 0.6;
    }
  `;
  
  const getTransitionGroupCss = (
    enterDuration: number,
    exitDuration: number,
    direction: Direction
  ) => css`
    display: grid;
  
    & > .item {
      grid-area: 1 / 1 / 2 / 2;
  
      &:not(:only-child) {
        &.${direction}-enter-active {
          transition: transform ${enterDuration}ms ease-out;
          z-index: 1;
        }
        &.${direction}-exit-active {
          transition: transform ${exitDuration}ms linear;
          z-index: 0;
        }
        .black-box {
          transition: opacity ${enterDuration}ms linear;
        }
      }
    }
  
    &.slide {
      overflow-x: hidden;
      ${getTransformStyles('translateX', '100%')}
    }
  
    &.vertical-slide {
      overflow-x: hidden;
      ${getTransformStyles('translateY', '100%')}
    }
  
    &.rotate {
      perspective: 2000px;
      & > .item {
        backface-visibility: hidden;
      }
      ${getTransformStyles('rotateY', '180deg')}
    }
  `;
  
  const isRouteElement = (element: ReactNode): element is RouteElement => {
    return isValidElement(element) && element.type === Route;
  };
  
  // from useRoutes:
  // https://github.com/remix-run/react-router/blob/f3d3e05ec00c6950720930beaf74fecbaf9dc5b6/packages/react-router/lib/hooks.tsx#L302
  const useNextPath = (pathname: string = '') => {
    const { matches: parentMatches } = useContext(UNSAFE_RouteContext);
    const routeMatch = parentMatches[parentMatches.length - 1];
    const parentPathnameBase = routeMatch ? routeMatch.pathnameBase : '/';
    return parentPathnameBase === '/'
      ? pathname
      : pathname.slice(parentPathnameBase.length) || '/';
  };
  
  const getMatch = (routes: RouteItem[], pathname: string) => {
    const matches = matchRoutes(routes, pathname);
    if (matches === null) {
      throw new Error(`Route ${pathname} does not match`);
    }
  
    const index = routes.findIndex((route) => {
      return matches.some((match) => match.route === route);
    });
    return { index, route: routes[index] };
  };
  
  export type SlideRoutesProps = {
    duration?: number;
    destroy?: boolean;
    children: ChildElement | (ChildElement | undefined | null)[];
    compare?: (a: RouteItem, b: RouteItem) => number;
  };
  
  const SlideRoutes = (props: SlideRoutesProps) => {
    const {
      duration = 200,
      destroy = true,
      compare,
      children,
    } = props;
  
    // routes
    const routeElements = Children.map(children, (child) => {
      if (!isRouteElement(child)) {
        return child;
      }
  
      const { element, ...restProps } = child.props;
      if (!element) {
        return child;
      }
  
      const nodeRef = createRef<HTMLDivElement>();
      const newElement = (
        <div className="item" ref={nodeRef}>
          {element}
        </div>
      );
      return { ...child, props: { ...restProps, element: newElement } };
    });
  
    const routes = createRoutesFromElements(routeElements) as RouteItem[];
    if (compare) {
      routes.sort(compare);
    }
  
    const location = useLocation();
    const routeList = useRoutes(routes, location);
  
    // direction
    const nextPath = useNextPath(location.pathname);
    const prevPath = useRef<string | null>(null);
  
    const direction = useRef<Direction>('undirected');
    const animation = useRef<"slide" | "vertical-slide">("slide");
  
    const nextMatch = getMatch(routes, nextPath);
  
    if (prevPath.current && prevPath.current !== nextPath) {
      const prevMatch = getMatch(routes, prevPath.current);
      const indexDiff = nextMatch.index - prevMatch.index;
  
      animation.current = "slide"
  
      if (indexDiff > 0) {
        direction.current = "forward";
      }
      else if (indexDiff < 0) {
        direction.current = "back";
      }
      else {
        direction.current = "undirected";
      }
    }
  
    prevPath.current = nextPath;
  
    // props
    const childFactory = useCallback(
      (child: ReactElement<CSSTransitionProps>) =>
        cloneElement(child, { classNames: direction.current }),
      []
    );
  
    const cssTransitionProps = useMemo(
      () => (destroy ? { timeout: duration } : { addEndListener() { } }),
      [destroy, duration]
    );
  
    return (
      <TransitionGroup
        className={`slide-routes ${animation.current}`}
        childFactory={childFactory}
        css={getTransitionGroupCss(duration, duration * 4, direction.current)}
      >
        <CSSTransition
          key={nextMatch.route.path ?? nextMatch.index}
          nodeRef={nextMatch.route.element.ref}
          {...cssTransitionProps}
          
        >
          {routeList}
        </CSSTransition>
      </TransitionGroup>
    );
  };
  
  export default SlideRoutes;
  
  /* 
  Modified from
  https://github.com/nanxiaobei/react-slide-routes
  
  MIT License
  
  Copyright (c) 2020 南小北
  
  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:
  
  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.
  
  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
  */